import {
  AlertTriangle,
  ChevronDown,
  Pencil,
  RotateCcw,
  Send
} from "lucide-react";
import { useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/status-badge";
import { Tag } from "@/components/ui/tag";
import { cn } from "@/lib/utils";

import type { Artifact } from "../../caseIde/types";
import type { InsightsFlow } from "../hooks/useInsightsFlow";
import type { KeyFactActions } from "../hooks/useKeyFactActions";
import {
  askKind,
  editorOf,
  headlineFlag,
  statusBadge,
  valueChips
} from "../model";
import type { KeyFact } from "../types";
import { AskBox, FactAsk } from "./FactAsk";
import { FactDetails } from "./FactDetails";
import { FactEditor } from "./FactEditor";

const VALUELESS: Partial<Record<KeyFact["status"], string>> = {
  needs_input: "Not set yet",
  checking: "Checking…"
};

const Headline: FC<{ fact: KeyFact }> = ({ fact }) => {
  const chips = valueChips(fact);
  if (chips)
    return (
      <ul className="mt-1 flex flex-wrap gap-1" aria-label={fact.label}>
        {chips.map((chip) => (
          <li key={chip} className="max-w-full min-w-0">
            <Tag title={chip}>
              <span className="min-w-0 truncate">{chip}</span>
            </Tag>
          </li>
        ))}
      </ul>
    );
  const empty =
    VALUELESS[fact.status] ??
    (fact.status === "failed" ? fact.error || "Couldn't compute" : null);
  if (empty || !fact.display)
    return <p className="mt-0.5 text-md text-fg-4">{empty ?? "Not set yet"}</p>;
  return (
    <p className="mt-0.5 line-clamp-2 text-md break-words text-fg">
      {fact.display}
    </p>
  );
};

/**
 * One fact: its label over the value as the headline, one status, a warn
 * line for a `must` flag, and a pencil that opens its editor. A fact that
 * needs the attorney asks right under it; Sources and Details opens below.
 */
export const FactRow: FC<{
  individualId: string;
  fact: KeyFact;
  /** Shown instead of the fact's label (an instance under its kind's row). */
  label?: string;
  flow: InsightsFlow;
  actions: KeyFactActions;
  onOpen: (artifact: Artifact) => void;
}> = ({ individualId, fact, label = fact.label, flow, actions, onOpen }) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const editor = editorOf(fact);
  const kind = askKind(fact, editor);
  const badge = statusBadge(fact);
  const flag = headlineFlag(fact);
  const editing = flow.editing === fact.key;
  const skipped = flow.skipped.has(fact.key);
  const requested = flow.requested.has(fact.key);
  const focused = flow.current?.key === fact.key;
  const busy = !!actions.busy?.startsWith(`${fact.key}:`);
  const canEdit =
    !fact.locked && fact.status !== "disabled" && fact.status !== "checking";
  const asking = kind && !editing && !skipped && !requested;

  return (
    <li
      ref={flow.register(fact.key)}
      className={cn(
        "scroll-mt-14 px-4 py-3",
        fact.status === "disabled" && "opacity-60"
      )}
      data-testid={`fact-row-${fact.key}`}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm text-fg-3">{label}</p>
          <Headline fact={fact} />
          {flag ? (
            <p className="mt-1 flex items-start gap-1.5 text-sm text-warn">
              <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden />
              {flag}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          <StatusBadge tone={badge.tone}>{badge.label}</StatusBadge>
          {canEdit ? (
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={`Edit ${label}`}
              aria-pressed={editing}
              onClick={() => flow.edit(editing ? null : fact.key)}
            >
              <Pencil />
            </Button>
          ) : null}
        </div>
      </div>

      {editing ? (
        <AskBox focused={focused} onFocus={() => flow.focus(fact.key)}>
          <p className="mb-2.5 text-base font-medium text-fg">Edit {label}</p>
          <FactEditor
            fact={fact}
            submitLabel="Save"
            busy={busy}
            onSubmit={(body) => void flow.respond(fact, body)}
            onCancel={() => flow.edit(null)}
            extra={
              fact.override ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="ml-auto"
                  disabled={busy}
                  onClick={() => void flow.respond(fact, { action: "revert" })}
                >
                  <RotateCcw /> Back to the Computed Value
                </Button>
              ) : null
            }
          />
        </AskBox>
      ) : null}

      {asking ? (
        <FactAsk
          fact={fact}
          kind={kind}
          focused={focused}
          busy={busy}
          handlers={{
            respond: (body) => void flow.respond(fact, body),
            edit: () => flow.edit(fact.key),
            skip: () => flow.skip(fact.key),
            focus: () => flow.focus(fact.key),
            upload: flow.upload ? () => flow.upload?.(fact) : undefined,
            request: flow.request ? () => flow.request?.(fact) : undefined
          }}
        />
      ) : null}

      {kind && requested && !editing ? (
        <p className="mt-2 flex flex-wrap items-center gap-x-2 text-sm text-fg-2">
          <Send className="size-3 text-fg-3" aria-hidden />
          Request drafted in the case chat.
          {flow.upload ? (
            <Button
              variant="link"
              size="xs"
              onClick={() => flow.upload?.(fact)}
            >
              Upload Now
            </Button>
          ) : null}
          <Button
            variant="link"
            size="xs"
            className="text-fg-3"
            onClick={() => flow.unrequest(fact.key)}
          >
            Undo
          </Button>
        </p>
      ) : null}

      {kind && skipped && !editing ? (
        <p className="mt-2 flex items-center gap-2 text-sm text-fg-3">
          Skipped for now.
          <Button
            variant="link"
            size="xs"
            onClick={() => flow.unskip(fact.key)}
          >
            Ask Again
          </Button>
        </p>
      ) : null}

      <Button
        variant="ghost"
        size="xs"
        className="mt-1.5 -ml-1.5 text-fg-3"
        aria-expanded={detailsOpen}
        onClick={() => setDetailsOpen((v) => !v)}
      >
        Sources and Details
        <ChevronDown
          className={cn("transition-transform", detailsOpen && "rotate-180")}
          aria-hidden
        />
      </Button>
      {detailsOpen ? (
        <FactDetails
          individualId={individualId}
          fact={fact}
          actions={actions}
          onOpen={onOpen}
        />
      ) : null}
    </li>
  );
};
